// The stage a presenter stands in: backgrounds, where the presenter is placed in the frame, and the name caption.
import { cropFor } from "./timeline";

export const BACKGROUNDS = { studio: "News studio", office: "Office", spotlight: "Spotlight", gradient: "Gradient", plain: "Plain colour", image: "My picture" };
export const POSITIONS = { center: "Centre", left: "Left", right: "Right" };
export const DEFAULT_STAGE = { background: "studio", colorA: "#0b5ed7", colorB: "#0a2540", position: "center", scale: 1 };
const FONT = '"Inter", "Segoe UI", Arial, sans-serif';

const hexToRgb = (hex) => { const n = parseInt(String(hex).replace("#", ""), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const rgba = (hex, a) => { const [r, g, b] = hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };
const mix = (hex, other, t) => { const a = hexToRgb(hex); const b = hexToRgb(other); return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(",")})`; };

// Where the presenter is: the centre of the face and the size of one face unit, in pixels.
export function presenterLayout(W, H, stage) {
  const portrait = H >= W * 1.05;
  const base = portrait ? Math.min(H * 0.1, W * 0.165) : Math.min(H * 0.165, W * 0.1);
  const size = base * (stage.scale || 1);
  const x = stage.position === "left" && !portrait ? W * 0.3 : stage.position === "right" && !portrait ? W * 0.7 : W / 2;
  const y = portrait ? H * 0.37 : H * (0.4 + (1 - (stage.scale || 1)) * 0.06);
  return { x, y, size };
}

// Where a picture goes in the frame: { x, y, w, h, whole }. A cut-out person (transparent picture) stands on the stage; any other picture fills the
// frame, or is shown whole over a blurred copy of itself when its shape is very different from the frame's.
export function photoPlacement(iw, ih, W, H, stage, hasAlpha) {
  if (hasAlpha) {
    const h = H * 1.04 * (stage.scale || 1);
    const w = (iw / ih) * h;
    const x = stage.position === "left" && W > H ? W * 0.3 - w / 2 : stage.position === "right" && W > H ? W * 0.7 - w / 2 : (W - w) / 2;
    return { x, y: H - h + H * 0.02, w, h, whole: false, cutout: true };
  }
  const frame = W / H;
  const shape = iw / ih;
  if (Math.abs(Math.log(shape / frame)) > 0.14) {
    const k = Math.min(W / iw, H / ih);
    return { x: (W - iw * k) / 2, y: (H - ih * k) / 2, w: iw * k, h: ih * k, whole: true, cutout: false };
  }
  const k = Math.max(W / iw, H / ih) * (stage.scale > 1 ? stage.scale : 1);
  return { x: (W - iw * k) / 2, y: (H - ih * k) / 2, w: iw * k, h: ih * k, whole: false, cutout: false };
}

function bokeh(ctx, W, H, time, a, b) {
  const spots = [[0.12, 0.2, 0.09], [0.22, 0.62, 0.06], [0.82, 0.18, 0.1], [0.9, 0.55, 0.07], [0.7, 0.8, 0.05], [0.35, 0.12, 0.05], [0.58, 0.3, 0.04]];
  spots.forEach(([x, y, r], i) => {
    const drift = Math.sin(time * 0.25 + i * 1.7) * 0.012;
    const cx = (x + drift) * W;
    const cy = (y + Math.cos(time * 0.2 + i) * 0.01) * H;
    const rad = r * Math.min(W, H);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
    g.addColorStop(0, rgba(i % 2 ? a : "#ffffff", 0.22));
    g.addColorStop(1, rgba(i % 2 ? a : "#ffffff", 0));
    ctx.fillStyle = g;
    ctx.fillRect(cx - rad, cy - rad, rad * 2, rad * 2);
  });
  void b;
}

function office(ctx, W, H, stage) {
  const wall = ctx.createLinearGradient(0, 0, 0, H);
  wall.addColorStop(0, mix("#f1f4f8", stage.colorA, 0.1));
  wall.addColorStop(1, mix("#c9d3df", stage.colorA, 0.16));
  ctx.fillStyle = wall;
  ctx.fillRect(0, 0, W, H);
  const u = Math.min(W, H);
  // a window with a city
  const wx = W * 0.05, wy = H * 0.12, ww = Math.min(W * 0.24, u * 0.5), wh = H * 0.46;
  const sky = ctx.createLinearGradient(0, wy, 0, wy + wh);
  sky.addColorStop(0, "#8fc4f2"); sky.addColorStop(1, "#e4f2fd");
  ctx.fillStyle = sky;
  ctx.fillRect(wx, wy, ww, wh);
  ctx.save();
  ctx.beginPath(); ctx.rect(wx, wy, ww, wh); ctx.clip();
  [[0.0, 0.5, 0.16], [0.14, 0.62, 0.14], [0.3, 0.45, 0.12], [0.43, 0.7, 0.18], [0.62, 0.52, 0.15], [0.78, 0.66, 0.22]].forEach(([x, top, w], i) => {
    ctx.fillStyle = mix("#7f9bb8", "#4d6a88", (i % 3) / 4);
    ctx.fillRect(wx + x * ww, wy + top * wh, w * ww, wh);
  });
  ctx.restore();
  ctx.strokeStyle = "#f8fafc"; ctx.lineWidth = Math.max(3, u * 0.008);
  ctx.strokeRect(wx, wy, ww, wh);
  ctx.beginPath(); ctx.moveTo(wx + ww / 2, wy); ctx.lineTo(wx + ww / 2, wy + wh); ctx.moveTo(wx, wy + wh / 2); ctx.lineTo(wx + ww, wy + wh / 2); ctx.stroke();
  // shelves with books and a plant
  const sx = W * 0.74, sw = Math.min(W * 0.22, u * 0.46);
  [0.3, 0.5].forEach((y, row) => {
    const sy = H * y;
    ctx.fillStyle = "#8b6b4a";
    ctx.fillRect(sx, sy, sw, u * 0.012);
    let bx = sx + sw * 0.04;
    ["#c0392b", "#2c6fb0", "#e0a526", "#3d8b6a", "#6c4a9c", "#d9772b", "#2b3a55"].forEach((color, i) => {
      const bh = u * (0.07 + ((i * 7 + row * 3) % 5) * 0.012);
      const bw = u * (0.016 + (i % 3) * 0.005);
      if (bx + bw < sx + sw * 0.96) { ctx.fillStyle = color; ctx.fillRect(bx, sy - bh, bw, bh); }
      bx += bw + u * 0.004;
    });
  });
  ctx.fillStyle = "#8b6b4a"; ctx.fillRect(sx, H * 0.7, sw, u * 0.012);
  const px = sx + sw * 0.5, py = H * 0.7;
  ctx.fillStyle = "#c26a46"; ctx.beginPath(); ctx.moveTo(px - u * 0.04, py - u * 0.07); ctx.lineTo(px + u * 0.04, py - u * 0.07); ctx.lineTo(px + u * 0.03, py); ctx.lineTo(px - u * 0.03, py); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#3f8f58";
  [[-0.03, -0.17, -0.5], [0.03, -0.18, 0.4], [0, -0.21, 0], [-0.05, -0.12, -0.9], [0.05, -0.12, 0.9]].forEach(([dx, dy, rot]) => {
    ctx.save(); ctx.translate(px + dx * u, py + dy * u); ctx.rotate(rot); ctx.beginPath(); ctx.ellipse(0, 0, u * 0.016, u * 0.06, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
  });
}

// Draws the stage background over the whole frame. `bg`: the visitor's own background picture (an Image) for the "image" stage.
export function drawStage(ctx, W, H, stage, bg, time = 0) {
  const kind = stage.background in BACKGROUNDS ? stage.background : "studio";
  if (kind === "image" && bg) {
    const [sx, sy, sw, sh] = cropFor(bg.naturalWidth || bg.width, bg.naturalHeight || bg.height, W, H, { zoom: 1.03 });
    ctx.drawImage(bg, sx, sy, sw, sh, 0, 0, W, H);
    ctx.fillStyle = "rgba(6,10,20,0.16)";
    ctx.fillRect(0, 0, W, H);
    return;
  }
  if (kind === "plain") { ctx.fillStyle = stage.colorA; ctx.fillRect(0, 0, W, H); return; }
  if (kind === "office") { office(ctx, W, H, stage); return; }
  if (kind === "spotlight") {
    ctx.fillStyle = "#04060c";
    ctx.fillRect(0, 0, W, H);
    const cx = W * (stage.position === "left" ? 0.3 : stage.position === "right" ? 0.7 : 0.5);
    const g = ctx.createRadialGradient(cx, H * 0.38, 0, cx, H * 0.38, Math.max(W, H) * 0.62);
    g.addColorStop(0, rgba(stage.colorA, 0.75)); g.addColorStop(0.55, rgba(stage.colorB, 0.35)); g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    return;
  }
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, stage.colorA);
  g.addColorStop(1, stage.colorB);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  if (kind === "gradient") {
    [[0.15, 0.2, 0.45], [0.85, 0.8, 0.5]].forEach(([x, y, r], i) => {
      const rad = r * Math.max(W, H);
      const rg = ctx.createRadialGradient(W * x, H * y, 0, W * x, H * y, rad);
      rg.addColorStop(0, `rgba(255,255,255,${i ? 0.08 : 0.14})`); rg.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);
    });
    return;
  }
  // the news studio: soft lights, a glowing screen wall and a dark floor
  bokeh(ctx, W, H, time, stage.colorA, stage.colorB);
  const wallH = H * 0.74;
  const panels = ctx.createLinearGradient(0, 0, 0, wallH);
  panels.addColorStop(0, "rgba(255,255,255,0.07)"); panels.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = panels;
  const count = W > H ? 6 : 3;
  for (let i = 0; i < count; i += 1) ctx.fillRect(W * (i / count) + W * 0.004, 0, W / count - W * 0.008, wallH);
  const floor = ctx.createLinearGradient(0, H * 0.74, 0, H);
  floor.addColorStop(0, "rgba(0,0,0,0.05)"); floor.addColorStop(1, "rgba(0,0,0,0.5)");
  ctx.fillStyle = floor;
  ctx.fillRect(0, H * 0.74, W, H * 0.26);
  ctx.strokeStyle = rgba(stage.colorA, 0.8); ctx.lineWidth = Math.max(2, H * 0.004);
  ctx.beginPath(); ctx.moveTo(0, H * 0.74); ctx.lineTo(W, H * 0.74); ctx.stroke();
}

// The name caption: slides in from the left after a moment, stays for a while, slides out. `t` = seconds from the start of the video.
export function drawNameCaption(ctx, W, H, name, role, t, accent = "#0b5ed7", { from = 0.7, hold = 5.5 } = {}) {
  if (!name && !role) return;
  const slide = 0.5;
  const local = t - from;
  if (local < 0 || local > hold + slide * 2) return;
  const inP = Math.min(1, local / slide);
  const outP = Math.min(1, Math.max(0, (local - hold - slide) / slide));
  const e = (x) => 1 - Math.pow(1 - x, 3);
  const move = e(inP) * (1 - e(outP));
  const u = Math.min(W, H);
  const nameSize = Math.round(u * 0.046);
  const roleSize = Math.round(u * 0.03);
  ctx.save();
  ctx.font = `800 ${nameSize}px ${FONT}`;
  const nameW = ctx.measureText(name).width;
  ctx.font = `500 ${roleSize}px ${FONT}`;
  const roleW = ctx.measureText(role).width;
  const pad = u * 0.026;
  const boxW = Math.max(nameW, roleW) + pad * 2 + u * 0.016;
  const boxH = (name ? nameSize * 1.3 : 0) + (role ? roleSize * 1.5 : 0) + pad * 1.1;
  const x = W * 0.05 - (1 - move) * (boxW + W * 0.05);
  const y = H - H * 0.14 - boxH;
  ctx.globalAlpha = move;
  ctx.fillStyle = "rgba(8,12,22,0.8)";
  ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x, y, boxW, boxH, u * 0.012) : ctx.rect(x, y, boxW, boxH); ctx.fill();
  const bar = ctx.createLinearGradient(0, y, 0, y + boxH);
  bar.addColorStop(0, accent); bar.addColorStop(1, "#22d3ee");
  ctx.fillStyle = bar;
  ctx.fillRect(x, y, u * 0.01, boxH);
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  let ty = y + pad * 0.6;
  if (name) { ctx.font = `800 ${nameSize}px ${FONT}`; ctx.fillStyle = "#fff"; ctx.fillText(name, x + pad + u * 0.01, ty); ty += nameSize * 1.3; }
  if (role) { ctx.font = `500 ${roleSize}px ${FONT}`; ctx.fillStyle = "#67e8f9"; ctx.fillText(role, x + pad + u * 0.01, ty); }
  ctx.restore();
}
